import React, { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import Axios from 'axios'
import {
	MagnifyingGlassIcon,
	PlayIcon,
	StopIcon,
	CheckCircleIcon,
	ArrowDownTrayIcon,
	TrashIcon,
	Cog8ToothIcon,
	XMarkIcon,
	ChevronUpDownIcon,
} from '@heroicons/react/24/solid'

import { baseURL } from '../../config'
import {
	saveSound as saveSoundToDb,
	listSounds,
	deleteSound,
	DEFAULT_SOUNDS as SERVER_DEFAULTS,
} from '../../utils/soundDb'
import { fetchDefaultSounds } from '../../services/httpService'

import impressed from '../../media/impressed.mp3'
import pristine from '../../media/pristine.mp3'

export const STORAGE_KEY = 'loadfetcher-notification-sound'
export const SOUND_CHANGED_EVENT = 'loadfetcher:notification-sound-changed'

const DEFAULT_SOUNDS = [
	{ id: 'impressed', name: 'impressed', url: impressed, bundled: true },
	{ id: 'pristine', name: 'pristine', url: pristine, bundled: true },
]

const SERVER_DEFAULT_ORDER = SERVER_DEFAULTS.map(s => s.id)

function formatSoundName(name = '') {
	const formatted = name.replace(/\b\w/g, char => char.toUpperCase())
	return formatted.length > 14 ? `${formatted.slice(0, 14)}...` : formatted
}

function ProgressCircle({ percent, colorClass }) {
	const r = 6
	const c = 2 * Math.PI * r
	const filled = c * (Math.max(0, Math.min(100, percent || 0)) / 100)
	const indeterminate = !percent
	return (
		<svg
			viewBox="0 0 16 16"
			className={`h-4 w-4 -rotate-90 ${colorClass} ${
				indeterminate ? 'animate-spin' : ''
			}`}
		>
			<circle
				cx="8"
				cy="8"
				r={r}
				stroke="currentColor"
				strokeWidth="2"
				fill="none"
				opacity="0.25"
			/>
			<circle
				cx="8"
				cy="8"
				r={r}
				stroke="currentColor"
				strokeWidth="2"
				fill="none"
				strokeLinecap="round"
				strokeDasharray={`${indeterminate ? c * 0.25 : filled} ${c}`}
			/>
		</svg>
	)
}

function getSavedSound() {
	try {
		const raw = localStorage.getItem(STORAGE_KEY)
		return raw ? JSON.parse(raw) : null
	} catch {
		return null
	}
}

export default function NotificationSoundPicker({ isDarkMode }) {
	const [query, setQuery] = useState('')
	const [results, setResults] = useState([])
	const [loading, setLoading] = useState(false)
	const [error, setError] = useState(null)
	const [playingId, setPlayingId] = useState(null)
	const [selected, setSelected] = useState(() => getSavedSound())
	const [downloadingId, setDownloadingId] = useState(null)
	const [downloadProgress, setDownloadProgress] = useState(0)
	const [librarySounds, setLibrarySounds] = useState([])
	const audioRef = useRef(null)
	const downloadedIds = new Set(librarySounds.map(s => s.id))

	const textColor = isDarkMode ? 'text-gray-300' : 'text-gray-700'
	const subTextColor = isDarkMode ? 'text-gray-400' : 'text-gray-500'
	const labelClass = `text-[15px] font-medium leading-none ${
		isDarkMode ? 'text-gray-200' : 'text-gray-800'
	}`
	const compactButtonClass = `flex h-8 w-8 shrink-0 items-center justify-center rounded-[10px] border transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500/30 disabled:cursor-not-allowed disabled:opacity-50 ${
		isDarkMode
			? 'border-white/10 bg-white/[0.03] text-gray-300 hover:bg-white/[0.08]'
			: 'border-gray-300 bg-white text-gray-600 hover:bg-gray-50'
	}`

	useEffect(() => {
		let cancelled = false
		;(async () => {
			await fetchDefaultSounds()
			if (cancelled) return
			const rows = await listSounds()
			if (cancelled) return
			setLibrarySounds(
				rows.map(r => ({
					id: r.id,
					name: r.name,
					author: r.author,
					duration: r.duration,
					url: URL.createObjectURL(r.blob),
				}))
			)
		})().catch(() => {})
		return () => {
			cancelled = true
			if (audioRef.current) {
				audioRef.current.pause()
				audioRef.current = null
			}
			librarySounds.forEach(s => s.url && URL.revokeObjectURL(s.url))
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	const runSearch = async e => {
		e.preventDefault()
		const q = query.trim()
		if (!q) {
			setResults([])
			return
		}
		setLoading(true)
		setError(null)
		try {
			const { data } = await Axios.get(`${baseURL}/api/audio-sound/search`, {
				params: { q },
			})
			setResults(data?.sounds || [])
		} catch (err) {
			setError(err.response?.data?.error || err.message || 'Search failed')
		} finally {
			setLoading(false)
		}
	}

	const togglePreview = sound => {
		if (audioRef.current) {
			audioRef.current.pause()
			audioRef.current = null
		}
		if (playingId === sound.id) {
			setPlayingId(null)
			return
		}
		const src = sound.url || sound.previewUrl
		const audio = new Audio(src)
		audio.volume = 1.0
		audio.onended = () => setPlayingId(null)
		audio.play().catch(() => setPlayingId(null))
		audioRef.current = audio
		setPlayingId(sound.id)
	}

	const downloadSound = async sound => {
		setDownloadingId(sound.id)
		setDownloadProgress(0)
		setError(null)
		try {
			const res = await Axios.get(
				`${baseURL}/api/audio-sound/${sound.id}/file`,
				{
					responseType: 'blob',
					onDownloadProgress: e => {
						if (e.total) {
							setDownloadProgress(Math.round((e.loaded / e.total) * 100))
						}
					},
				}
			)
			await saveSoundToDb({
				id: sound.id,
				name: sound.name,
				author: sound.author,
				duration: sound.duration,
				license: sound.license,
				blob: res.data,
			})
			const objectUrl = URL.createObjectURL(res.data)
			setLibrarySounds(prev => [
				...prev,
				{
					id: sound.id,
					name: sound.name,
					author: sound.author,
					duration: sound.duration,
					url: objectUrl,
				},
			])
		} catch (err) {
			setError(err.response?.data?.error || err.message || 'Download failed')
		} finally {
			setDownloadingId(null)
			setDownloadProgress(0)
		}
	}

	const removeFromLibrary = async sound => {
		try {
			await deleteSound(sound.id)
			setLibrarySounds(prev => {
				const target = prev.find(s => s.id === sound.id)
				if (target?.url) URL.revokeObjectURL(target.url)
				return prev.filter(s => s.id !== sound.id)
			})
			if (selected?.id === sound.id) clearChoice()
		} catch (err) {
			setError(err.message || 'Remove failed')
		}
	}

	const choose = sound => {
		const payload = {
			id: sound.id,
			name: sound.name,
			author: sound.author,
		}
		if (sound.bundled) payload.url = sound.url
		localStorage.setItem(STORAGE_KEY, JSON.stringify(payload))
		setSelected(payload)
		window.dispatchEvent(new CustomEvent(SOUND_CHANGED_EVENT, { detail: payload }))
	}

	const clearChoice = () => {
		localStorage.removeItem(STORAGE_KEY)
		setSelected(null)
		window.dispatchEvent(new CustomEvent(SOUND_CHANGED_EVENT, { detail: null }))
	}

	const [modalOpen, setModalOpen] = useState(false)

	const sortedLibrary = (() => {
		const defaultsByOrder = SERVER_DEFAULT_ORDER
			.map(id => librarySounds.find(s => s.id === id))
			.filter(Boolean)
		const userSaved = librarySounds.filter(s => !SERVER_DEFAULT_ORDER.includes(s.id))
		return [...defaultsByOrder, ...userSaved]
	})()

	const allSounds = [...DEFAULT_SOUNDS, ...sortedLibrary]
	const list = query.trim() ? results : allSounds
	const selectedId = selected?.id ?? 'impressed'
	const selectedSound = allSounds.find(s => String(s.id) === String(selectedId))
	const selectedLabel = formatSoundName(selectedSound?.name || selected?.name || 'impressed')
	const isSelectedPlaying = selectedSound && playingId === selectedSound.id

	const closeModal = () => {
		if (audioRef.current) {
			audioRef.current.pause()
			audioRef.current = null
			setPlayingId(null)
		}
		setModalOpen(false)
	}

	return (
		<>
			<div className="flex items-center justify-between gap-5">
				<label className={labelClass}>
					Notification sound
				</label>
				<div className="flex w-[192px] items-center gap-1.5">
					<div className="relative min-w-0">
						<div
							className={`pointer-events-none flex h-8 w-[116px] items-center justify-between rounded-[10px] border px-2.5 ${
								isDarkMode
									? 'border-white/10 bg-white/[0.03] text-gray-100'
									: 'border-gray-300 bg-white text-gray-800'
							}`}
						>
							<span className="block min-w-0 truncate text-xs font-semibold leading-none">
								{selectedLabel}
							</span>
							<ChevronUpDownIcon className="ml-1 h-3.5 w-3.5 shrink-0 text-gray-500" />
						</div>
						<select
							value={selectedId}
							onChange={e => {
								const id = e.target.value
								const sound = allSounds.find(s => String(s.id) === id)
								if (sound) choose(sound)
							}}
							className="absolute inset-0 h-8 w-[116px] cursor-pointer opacity-0"
						>
							{allSounds.map(s => (
								<option key={s.id} value={s.id}>
									{formatSoundName(s.name)}
								</option>
							))}
						</select>
					</div>
					<button
						type="button"
						onClick={() => selectedSound && togglePreview(selectedSound)}
						disabled={!selectedSound}
						className={compactButtonClass}
						title={isSelectedPlaying ? 'Stop preview' : 'Preview sound'}
					>
						{isSelectedPlaying ? (
							<StopIcon className="h-4 w-4" />
						) : (
							<PlayIcon className="h-4 w-4" />
						)}
					</button>
					<button
						type="button"
						onClick={() => setModalOpen(true)}
						className={compactButtonClass}
						title="Manage notification sounds"
					>
						<Cog8ToothIcon className="h-5 w-5" />
					</button>
				</div>
			</div>

			{modalOpen && createPortal(
				<div
					className="fixed inset-0 z-[20000] flex items-start justify-center bg-black/40 px-4 pt-16"
					onMouseDown={e => e.stopPropagation()}
					onClick={closeModal}
				>
					<div
						className={`${
							isDarkMode ? 'bg-gray-800' : 'bg-white'
						} w-full max-w-md max-h-[calc(100vh-5rem)] overflow-hidden flex flex-col rounded-xl shadow-2xl`}
						onClick={e => e.stopPropagation()}
					>
						<div
							className={`flex items-center justify-between p-4 border-b ${
								isDarkMode ? 'border-gray-700' : 'border-gray-200'
							}`}
						>
							<h2
								className={`text-lg font-semibold ${
									isDarkMode ? 'text-white' : 'text-gray-900'
								}`}
							>
								Notification sounds
							</h2>
							<button
								onClick={closeModal}
								className={`p-1 rounded hover:opacity-80 ${textColor}`}
								title="Close"
							>
								<XMarkIcon className="h-5 w-5" />
							</button>
						</div>

						<div
							className={`p-4 overflow-y-auto notification-sounds-scroll ${
								isDarkMode
									? 'notification-sounds-scroll-dark'
									: 'notification-sounds-scroll-light'
							}`}
						>
							<form onSubmit={runSearch} className="flex items-center gap-2 mb-3">
				<div className="relative flex-1">
					<MagnifyingGlassIcon
						className={`absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 ${subTextColor}`}
					/>
					<input
						type="text"
						value={query}
						onChange={e => setQuery(e.target.value)}
						placeholder="Search more sounds..."
						className={`h-8 w-full pl-7 pr-7 text-sm border rounded focus:outline-none ${
							isDarkMode
								? 'bg-gray-700 border-gray-500 text-white'
								: 'bg-white border-gray-300 text-black'
						}`}
					/>
					{query && (
						<button
							type="button"
							onClick={() => {
								setQuery('')
								setResults([])
							}}
							className={`absolute right-2 top-1/2 -translate-y-1/2 p-0.5 rounded hover:opacity-80 ${subTextColor}`}
							title="Clear"
						>
							<XMarkIcon className="h-4 w-4" />
						</button>
					)}
				</div>
				<button
					type="submit"
					className="h-8 px-3 text-xs font-medium rounded bg-blue-600 text-white transition hover:bg-blue-700"
				>
					Search
				</button>
			</form>

			{error && (
				<div className="text-xs text-red-500 mb-2">{error}</div>
			)}

			<div
				className={`h-[360px] overflow-y-auto border rounded notification-sounds-scroll ${
					isDarkMode
						? 'border-gray-600 notification-sounds-scroll-dark'
						: 'border-gray-200 notification-sounds-scroll-light'
				}`}
			>
				{loading && list.length === 0 ? (
					<div className={`flex items-center justify-center h-full text-xs ${subTextColor}`}>Loading…</div>
				) : list.length === 0 ? (
					<div className={`flex items-center justify-center h-full text-xs ${subTextColor}`}>
						{query.trim() ? 'No results' : 'No sounds available'}
					</div>
				) : (
					list.map(sound => {
						const isBundled = sound.bundled === true
						const isDefault = isBundled || SERVER_DEFAULT_ORDER.includes(sound.id)
						const isSelected = selected
							? selected.id === sound.id
							: sound.id === 'impressed'
						const isPlaying = playingId === sound.id
						const isDownloaded = isBundled || downloadedIds.has(sound.id)
						const isDownloading = downloadingId === sound.id
						return (
							<div
								key={sound.id}
								className={`flex items-center gap-2 px-2 py-1.5 text-xs border-b last:border-b-0 ${
									isDarkMode ? 'border-gray-700' : 'border-gray-100'
								} ${isSelected ? (isDarkMode ? 'bg-gray-700' : 'bg-blue-50') : ''}`}
							>
								<button
									onClick={() => togglePreview(sound)}
									className={`p-1 rounded hover:opacity-80 ${textColor}`}
									title={isPlaying ? 'Stop' : 'Preview'}
								>
									{isPlaying ? (
										<StopIcon className="h-4 w-4" />
									) : (
										<PlayIcon className="h-4 w-4" />
									)}
								</button>
								<div className="flex-1 min-w-0">
									<div className={`truncate ${textColor}`}>{sound.name}</div>
								</div>
								{!isDefault && (
									isDownloaded ? (
										<button
											onClick={() => removeFromLibrary(sound)}
											className={`p-1 rounded hover:opacity-80 text-red-400`}
											title="Remove from library"
										>
											<TrashIcon className="h-4 w-4" />
										</button>
									) : isDownloading ? (
										<div className="p-1" title="Downloading…">
											<ProgressCircle
												percent={downloadProgress}
												colorClass={textColor}
											/>
										</div>
									) : (
										<button
											onClick={() => downloadSound(sound)}
											className={`p-1 rounded hover:opacity-80 ${textColor}`}
											title="Save to library"
										>
											<ArrowDownTrayIcon className="h-4 w-4" />
										</button>
									)
								)}
								<button
									onClick={() => isDownloaded && choose(sound)}
									disabled={!isDownloaded}
									className={`p-1 rounded ${
										isSelected
											? 'text-blue-500'
											: isDownloaded
											? textColor
											: 'opacity-30 cursor-not-allowed'
									}`}
									title={
										isSelected
											? 'Selected'
											: isDownloaded
											? 'Use this sound'
											: 'Save to library first'
									}
								>
									<CheckCircleIcon className="h-5 w-5" />
								</button>
							</div>
						)
					})
				)}
			</div>
						</div>
					</div>
				</div>
				,
				document.body
			)}
		</>
	)
}
